import { useState } from 'react'
import { router } from '@inertiajs/react'

import { TextInput } from '@atoms'
import type { TextInputProps } from '@atoms/form/TextInput'
import { Textarea } from '@atoms/form/Textarea'
import { FormDialog } from '@components/molecules/FormDialog'
import { RadioGroupField } from '@components/molecules/RadioGroupField'
import { SelectField } from '@components/molecules/SelectField'
import { scopedTranslation } from '@utils/I18n'

const t = scopedTranslation('views.call_lists.new')

const SKIP_PRESETS = [
  { value: '24', label: t('skip_presets.one_day') },
  { value: '72', label: t('skip_presets.three_days') },
  { value: '168', label: t('skip_presets.one_week') },
]

const revealOnMount = (element: HTMLElement | null) => element?.scrollIntoView({ block: 'nearest' })

export type CreateCallList = {
  href: string
  groups?: Array<{ id: string; name: string }>
  groupId?: string | null
  recentContactHint: string
}

type AddCallListDialogProps = CreateCallList & { onClose: () => void }

export function AddCallListDialog({
  href,
  groups,
  groupId: initialGroupId,
  recentContactHint,
  onClose,
}: AddCallListDialogProps) {
  const [groupId, setGroupId] = useState(initialGroupId ?? null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [skipPreset, setSkipPreset] = useState('24')
  const [customDays, setCustomDays] = useState('')
  const hours = skipPreset === 'custom' ? customDays && String(Number(customDays) * 24) : skipPreset
  const [busy, setBusy] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const fieldErrors: TextInputProps['errors'] = Object.fromEntries(
    Object.entries(errors).map(([field, message]) => [field, { type: 'server', message }])
  )
  const groupOptions = groups?.map((group) => ({ value: group.id, label: group.name }))
  const groupField = { name: 'action_group_id', label: t('labels.group'), required: true, errors: fieldErrors }

  const chooseSkip = (next: string) => {
    if (next === 'custom' && skipPreset !== 'custom') setCustomDays(String(Number(skipPreset) / 24))
    setSkipPreset(next)
  }

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    if (hours === '') return setErrors({ number_of_hours: t('errors.days_blank') })
    router.post(
      href,
      { call_list: { action_group_id: groupId ?? undefined, name, description, number_of_hours: hours } },
      {
        preserveScroll: true,
        preserveState: 'errors',
        onStart: () => setBusy(true),
        onError: (serverErrors) => {
          setErrors(serverErrors)
          setBusy(false)
        },
        onHttpException: () => setBusy(false),
        onNetworkError: () => setBusy(false),
      }
    )
  }

  return (
    <FormDialog.Root open onOpenChange={(open) => !open && onClose()} size="lg" position="top">
      <FormDialog.Title>{t('title')}</FormDialog.Title>
      <FormDialog.Form onSubmit={submit}>
        <FormDialog.Body>
          {groupOptions &&
            (groupOptions.length < 5 ? (
              <RadioGroupField {...groupField} options={groupOptions} value={groupId} onValueChange={setGroupId} />
            ) : (
              <SelectField {...groupField} options={groupOptions} value={groupId} onValueChange={setGroupId} />
            ))}
          <TextInput
            name="name"
            label={t('labels.name')}
            autoFocus={!groupOptions || Boolean(initialGroupId)}
            required
            errors={fieldErrors}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          <Textarea
            name="description"
            label={t('labels.description')}
            rows={3}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
          <div className="tw:space-y-3">
            <RadioGroupField
              appearance="tiles"
              label={t('labels.skip_recent')}
              required
              options={[
                ...SKIP_PRESETS,
                {
                  value: 'custom',
                  label: t('skip_presets.custom'),
                  whenSelected: (
                    <div ref={revealOnMount} className="tw:mt-2 tw:flex tw:items-start tw:gap-2 tw:scroll-mb-28">
                      <div className="tw:w-24">
                        <TextInput
                          name="number_of_hours"
                          type="number"
                          min={0}
                          max={30}
                          label={t('labels.days')}
                          hideLabel
                          errors={fieldErrors}
                          value={customDays}
                          onChange={(event) => setCustomDays(event.target.value)}
                        />
                      </div>
                      <span className="tw:pt-2.5 tw:text-sm tw:text-gray-600">{t('hints.days')}</span>
                    </div>
                  ),
                },
              ]}
              value={skipPreset}
              onValueChange={chooseSkip}
            />
            <details className="tw:text-sm tw:text-gray-600">
              <summary className="tw:w-fit tw:cursor-pointer tw:rounded-sm tw:font-medium tw:text-primary-500 tw:focus-visible:outline-hidden tw:focus-visible:ring-2 tw:focus-visible:ring-primary-500">
                {t('hints.how_skipping_works')}
              </summary>
              <p className="tw:mt-2 tw:mb-0">{t('hints.skip_window')}</p>
              <p className="tw:mt-2 tw:mb-0">{recentContactHint}</p>
            </details>
          </div>
        </FormDialog.Body>
        <FormDialog.Footer submitText={t('submit')} isLoading={busy} />
      </FormDialog.Form>
    </FormDialog.Root>
  )
}
